import { z } from 'zod';
/**
 * Underwriting decisions (tracker task 64, decision 0038). After review the underwriter records an
 * outcome with a reason. Accepting with conditions sets dated conditions the business must meet: each one
 * is reminded before it is due (as an obligation), the business sends evidence, and the underwriter
 * clears it, sends it back, or waives it. Refer keeps the assessment in review (for a senior underwriter);
 * the other outcomes decide it.
 */
export const DECISION_OUTCOMES = ['accept', 'accept_with_conditions', 'refer', 'decline'];
/** Wording to confirm with the pilot insurer. */
export const DECISION_LABEL = {
    accept: 'Accept',
    accept_with_conditions: 'Accept with conditions',
    refer: 'Refer',
    decline: 'Decline',
};
/** What the business is told. Referrals stay internal until decided. */
export const DECISION_FOR_BUSINESS = {
    accept: 'Your insurer accepted your Cyber Check.',
    accept_with_conditions: 'Your insurer accepted your Cyber Check, with conditions to meet by the dates below.',
    decline: 'Your insurer has declined cover on this Cyber Check. Contact your insurer or broker to talk it through.',
};
export const decides = (o) => o !== 'refer';
export const statusAfter = (o) => (decides(o) ? 'decided' : 'in_review');
/** Reminders before a condition is due. */
export const CONDITION_REMINDER_OFFSETS = ['T-14', 'T-7', 'T-1'];
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DateString = z
    .string()
    .regex(ISO_DATE, 'YYYY-MM-DD')
    .refine((v) => !Number.isNaN(Date.parse(`${v}T00:00:00Z`)), 'not a date');
const Text = (max) => z.string().trim().min(1).max(max);
export const NewCondition = z.strictObject({
    title: Text(200),
    detail: Text(2000).optional(),
    /** A control the condition is about (e.g. AC-01), so the business's missions can point at it. */
    control: z
        .string()
        .regex(/^[A-Z]{2,4}-\d{2}$/)
        .optional(),
    due_date: DateString,
});
export const CreateDecision = z
    .strictObject({
    outcome: z.enum(DECISION_OUTCOMES),
    /** For the insurer's file; the business doesn't see it. */
    reason: Text(2000),
    conditions: z.array(NewCondition).max(20).default([]),
})
    .superRefine((d, ctx) => {
    if (d.outcome === 'accept_with_conditions' && d.conditions.length === 0)
        ctx.addIssue({ code: 'custom', path: ['conditions'], message: 'Add at least one condition' });
    if (d.outcome !== 'accept_with_conditions' && d.conditions.length > 0)
        ctx.addIssue({ code: 'custom', path: ['conditions'], message: 'Only “Accept with conditions” sets conditions' }); // prettier-ignore
});
/** The business shows a condition is met: what it did, and any documents it uploaded for it. */
export const ConditionEvidence = z.strictObject({
    note: Text(2000),
    document_ids: z.array(z.uuid()).max(10).default([]),
});
/** The underwriter's answer to evidence: clear it, send it back (with what is missing), or waive it. */
export const ConditionReview = z
    .strictObject({ action: z.enum(['clear', 'reopen', 'waive']), note: Text(2000).optional() })
    .superRefine((r, ctx) => {
    if (r.action !== 'clear' && !r.note)
        ctx.addIssue({ code: 'custom', path: ['note'], message: 'Say what is missing, or why it is waived' });
});
export const CONDITION_STATES = ['open', 'evidenced', 'cleared', 'waived'];
export function conditionDisplay(c, today) {
    return c.state === 'open' && c.due_date < today ? 'overdue' : c.state;
}
export const CONDITION_LABEL = {
    open: 'To do',
    overdue: 'Overdue',
    evidenced: 'Evidence sent',
    cleared: 'Met',
    waived: 'Waived',
};
export const conditionDone = (s) => s === 'cleared' || s === 'waived';
//# sourceMappingURL=decisions.js.map